/**
 * Clip a corner 45 degrees (PRP-WEB-2 W1.2, DD3: chamfer only; bays, 22.5 degree walls
 * and curves are deferred).
 *
 * The square corner C between walls P->C and C->Q becomes two corners, C1 on P->C and
 * C2 on C->Q, each `legFt` back from C, joined by a 45 degree wall C1->C2. C keeps its id
 * as C2, so the wall C->Q keeps its id, thickness and openings; C1 is new and the
 * diagonal takes the thickness of P->C.
 *
 * Openings keep their absolute positions: those on P->C are unchanged (their offsets run
 * from P); those on C->Q move back by the leg (their offsets run from C, which moved). An
 * opening the cut would reach refuses the whole chamfer, naming it, and nothing changes.
 * A measurement on C keeps the corner that still sits on its axis (C2 or C1), so every
 * reading stays true to the drawing.
 */
import { POLICY } from './policy';
import { EPS, axisOf, dist, findRoom, round6, tidy, unit } from './geom';
import { copyWall } from './thickness';
import { uid } from './plan';
import type { Axis, Fail, Plan } from './types';

export type ChamferResult =
  | { ok: true; vid: number }
  | Fail<'missing' | 'not-square' | 'too-long' | 'opening'>;

/**
 * Replace the square corner at index `cornerIdx` of room `rid` with a 45 degree wall
 * whose two legs run `legFt` (default `chamfer_leg_default_ft`, 2 ft) back along the
 * two walls. One undoable step under `History.run`. Openings on the two cut walls
 * keep their absolute positions; one that no longer fits refuses the whole chamfer.
 * On ok, `vid` is the new diagonal wall's start corner id.
 */
export function chamferCorner(plan: Plan, rid: string, cornerIdx: number, legFt: number = POLICY.chamfer_leg_default_ft): ChamferResult {
  const r = findRoom(plan, rid);
  const n = r ? r.corners.length : 0;
  if (!r || !Number.isInteger(cornerIdx) || cornerIdx < 0 || cornerIdx >= n) {
    return { ok: false, err: 'missing', message: 'That corner is gone' };
  }
  const P = r.corners[(cornerIdx - 1 + n) % n], C = r.corners[cornerIdx], Q = r.corners[(cornerIdx + 1) % n];
  const t1 = axisOf(P, C), t2 = axisOf(C, Q);
  if (!((t1 === 'h' && t2 === 'v') || (t1 === 'v' && t2 === 'h'))) {
    return { ok: false, err: 'not-square', message: 'Only a square corner can be clipped' };
  }
  const leg = legFt;
  const L1 = dist(P, C), L2 = dist(C, Q);
  if (!(Number.isFinite(leg) && leg > EPS) || leg >= L1 - EPS || leg >= L2 - EPS) {
    return { ok: false, err: 'too-long', message: 'The clip is longer than a wall at that corner' };
  }
  for (const o of r.openings) {
    const name = o.kind === 'missing' ? 'opening' : o.kind;
    if (o.edgeStart === P.id && o.offFt + o.widthFt > L1 - leg + EPS) {
      return { ok: false, err: 'opening', message: `A ${name} is too close to that corner. Move it first.` };
    }
    if (o.edgeStart === C.id && o.offFt < leg - EPS) {
      return { ok: false, err: 'opening', message: `A ${name} is too close to that corner. Move it first.` };
    }
  }
  const uP = unit(C, P), uQ = unit(C, Q);
  const C1 = { id: uid(plan), x: round6(C.x + uP.x * leg), y: round6(C.y + uP.y * leg) };
  r.corners.splice(cornerIdx, 0, C1);
  C.x = round6(C.x + uQ.x * leg);
  C.y = round6(C.y + uQ.y * leg);
  copyWall(r, P.id, C1.id);
  for (const o of r.openings) if (o.edgeStart === C.id) o.offFt = round6(o.offFt - leg);
  // C (now C2) moved along C->Q: it keeps the coordinate across that wall; C1 keeps the other.
  const keptByC2: Axis = t2 === 'h' ? 'y' : 'x';
  const repoint = (m: { axis: Axis; a: number; b: number }) => {
    if (m.axis === keptByC2) return;
    if (m.a === C.id) m.a = C1.id;
    if (m.b === C.id) m.b = C1.id;
  };
  r.measurements.forEach(repoint);
  for (const q of plan.rooms) q.spans?.forEach(repoint);
  tidy(plan);
  return { ok: true, vid: C1.id };
}
